import React, { useState, useEffect, useCallback } from "react";

const ViewResumes = ({ roles, fetchResumes, onDeleteResume, onDeleteAllResumes }) => {
  const [selectedRole, setSelectedRole] = useState("");
  const [resumeList, setResumeList] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadInitialResumes = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchResumes(selectedRole, 1, 10);
      setResumeList(result.resumes);
      setPagination(result.pagination);
    } catch (error) {
      console.error("Error loading resumes:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedRole, fetchResumes]);

  useEffect(() => {
    if (selectedRole) {
      loadInitialResumes();
    } else {
      setResumeList([]);
      setPagination(null);
    }
  }, [selectedRole, loadInitialResumes]);

  const loadMoreResumes = async () => {
    if (!pagination || !pagination.has_more) return;

    setLoadingMore(true);
    try {
      const nextPage = pagination.current_page + 1;
      const result = await fetchResumes(selectedRole, nextPage, 10);
      setResumeList(prev => [...prev, ...result.resumes]);
      setPagination(result.pagination);
    } catch (error) {
      console.error("Error loading more resumes:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleDeleteResume = async (resume) => {
    const fileName = resume.file_name || `${resume.id}.pdf`; // Use actual filename or fallback to .pdf
    const success = await onDeleteResume(selectedRole, fileName);
    if (success) {
      // Remove the deleted resume from the list
      setResumeList(prev => prev.filter(r => r.id !== resume.id));
      // Update pagination count
      if (pagination) {
        setPagination(prev => ({
          ...prev,
          total_resumes: prev.total_resumes - 1
        }));
      }
    }
  };

  const handleDeleteAllResumes = async () => {
    const success = await onDeleteAllResumes(selectedRole);
    if (success) {
      setResumeList([]);
      setPagination(null);
    }
  };

  return (
    <div className="flex justify-center w-full p-4 sm:p-6 md:p-10 min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-lg p-6 sm:p-8 md:p-10 border border-gray-200">

        <div className="mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">View Resumes</h2>
          <p className="text-sm text-gray-600">Browse and manage resumes by role</p>
        </div>

        <div className="mb-6">
          <label className="text-sm font-semibold text-gray-700 block mb-3">Select Role</label>
          <select
            className="w-full border-2 border-gray-300 rounded-xl p-3.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white hover:border-gray-400"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
          >
            <option value="">Choose Role</option>
            {roles.map((r) => (
              <option key={r} value={r}>{r.slice(0, -1)}</option>
            ))}
          </select>
        </div>

        {/* DELETE ALL BUTTON */}
        {selectedRole && resumeList.length > 0 && (
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="text-sm text-gray-600">
              {pagination && `${pagination.total_resumes} resume${pagination.total_resumes !== 1 ? 's' : ''} found`}
            </div>
            <button
              onClick={handleDeleteAllResumes}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-all hover:shadow-lg transform hover:-translate-y-0.5"
            >
              🗑️ Delete All Resumes
            </button>
          </div>
        )}

        {/* LOADING STATE */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
            <p className="text-gray-600 mt-4 text-base font-medium">Loading resumes...</p>
          </div>
        )}

        {/* EMPTY STATE */}
        {!loading && !selectedRole && (
          <div className="text-center py-16 px-4">
            <div className="text-6xl mb-4">📄</div>
            <p className="text-gray-500 text-lg font-medium">Please select a role to view resumes</p>
          </div>
        )}

        {/* SCROLLABLE RESUME LIST */}
        {!loading && selectedRole && (
          <div className="divide-y divide-gray-200 max-h-[55vh] overflow-y-auto pr-2 custom-scrollbar">
            {resumeList.length === 0 ? (
              <div className="text-center py-16 px-4">
                <div className="text-6xl mb-4">📭</div>
                <p className="text-gray-500 text-lg font-medium">No resumes found for this role</p>
                <p className="text-gray-400 text-sm mt-2">Upload some resumes to get started</p>
              </div>
            ) : (
              resumeList.map((resume, idx) => (
                <div
                  key={idx}
                  className="group py-4 hover:bg-gray-50 transition-colors flex flex-col sm:flex-row justify-between items-start gap-3"
                >
                  <div className="flex-1 w-full sm:w-auto min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <p className="font-semibold text-gray-900 text-base break-all">{resume.id}</p>
                      {resume.file_extension && (
                        <span className="text-xs bg-orange-500 text-white px-2.5 py-1 rounded-full font-semibold">
                          {resume.file_extension.toUpperCase().replace('.', '')}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                      {resume.resume_text.slice(0, 180)}...
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteResume(resume)}
                    className="text-red-600 hover:text-red-700 transition-colors flex-shrink-0"
                    title="Delete resume"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {/* LOAD MORE BUTTON */}
        {!loading && pagination && pagination.has_more && (
          <div className="mt-6 text-center">
            <button
              onClick={loadMoreResumes}
              disabled={loadingMore}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-blue-400 disabled:to-blue-500 text-white px-8 py-3 rounded-xl text-sm font-semibold transition-all hover:shadow-lg transform hover:-translate-y-0.5 disabled:transform-none"
            >
              {loadingMore ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Loading...
                </span>
              ) : (
                `⬇️ Load More (${pagination.total_resumes - resumeList.length} remaining)`
              )}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default ViewResumes;
